import type { GridFilterItem } from '@mui/x-data-grid-pro';

import { chipDisplayValue } from './dateFilterFormat';
import type { AdvancedFilter } from '../components/molecules/FilterBar';
import type { FilterFieldDef } from '../components/molecules/FilterBar/AddFilterModal.types';

/**
 * Rebuilds the Add Filter chips from a `GridFilterModel`.
 *
 * Every list page needs this to reopen a saved report or a deep-linked URL: the
 * persisted artefact is a flat list of wire predicates, and the chip strip has
 * to be reconstructed from it. Six pages had grown their own copy of the walk,
 * and they had already drifted — see `chipDisplayValue` below.
 *
 * Pages keep ownership of their *quick-filter* slots: match those first and
 * pass only what is left here. That split is deliberate — which predicates
 * belong to a dropdown is page knowledge, whereas rebuilding a chip is not.
 */

/** A `between` chip is stored as two predicates. These are the halves. */
const BETWEEN_FROM_OPS = new Set(['onOrAfter', '>=']);
const BETWEEN_TO_OPS = new Set(['onOrBefore', '<=']);

const chip = (item: GridFilterItem, def: FilterFieldDef | undefined): AdvancedFilter => ({
  field: def?.name ?? item.field,
  dataKey: item.field,
  type: def?.type ?? 'text',
  operator: item.operator,
  value: String(item.value),
  display: `${def?.name ?? item.field} ${item.operator} ${chipDisplayValue(def, String(item.value))}`,
});

/**
 * @param items    the predicates left after the page has claimed its quick-filter slots
 * @param fieldDefs the page's Add Filter catalog, used for the chip's label and type
 *
 * Chip order is preserved exactly as the per-page copies produced it: plain
 * predicates in encounter order, then merged `between` chips, then any
 * unpaired half. Reordering would shuffle a user's chip strip on every reload.
 */
export const advancedFiltersFromModel = (
  items: GridFilterItem[],
  fieldDefs: readonly FilterFieldDef[]
): AdvancedFilter[] => {
  const defOf = (field: string): FilterFieldDef | undefined =>
    fieldDefs.find((f) => f.key === field);

  const advancedFilters: AdvancedFilter[] = [];
  // Bounds are collected as lists, not one-per-field. Nothing stops a user
  // adding two `between` chips on the same field — FilterBar appends chips
  // without deduping — and that emits two lower and two upper bounds. Keeping
  // only the last of each would silently drop one of their filters.
  const betweenFrom = new Map<string, GridFilterItem[]>();
  const betweenTo = new Map<string, GridFilterItem[]>();

  const collect = (into: Map<string, GridFilterItem[]>, item: GridFilterItem): void => {
    const found = into.get(item.field);
    if (found) found.push(item);
    else into.set(item.field, [item]);
  };

  for (const item of items) {
    // Drop valueless predicates — they would render a chip with no text after
    // the operator. `0` and `false` are real values and must survive, so this
    // cannot be a truthiness check.
    if (item.value == null || item.value === '') continue;
    if (BETWEEN_FROM_OPS.has(item.operator)) {
      collect(betweenFrom, item);
    } else if (BETWEEN_TO_OPS.has(item.operator)) {
      collect(betweenTo, item);
    } else {
      advancedFilters.push(chip(item, defOf(item.field)));
    }
  }

  const paired = new Set<GridFilterItem>();

  // Pair bounds off in order: the nth lower bound on a field belongs with the
  // nth upper bound, which is the order buildFilterModel emitted them in.
  for (const [field, fromItems] of betweenFrom) {
    const toItems = betweenTo.get(field) ?? [];
    const def = defOf(field);
    const type = def?.type ?? 'text';

    for (let i = 0; i < Math.min(fromItems.length, toItems.length); i += 1) {
      paired.add(fromItems[i]);
      paired.add(toItems[i]);
      const fromVal = String(fromItems[i].value);
      const toVal = String(toItems[i].value);

      advancedFilters.push({
        field: def?.name ?? field,
        dataKey: field,
        type,
        operator: 'between',
        value: fromVal,
        value2: toVal,
        // The date pickers read these; a numeric range leaves them undefined.
        dateFrom: type === 'date' ? fromVal : undefined,
        dateTo: type === 'date' ? toVal : undefined,
        display: `${def?.name ?? field} between ${chipDisplayValue(def, fromVal)} and ${chipDisplayValue(def, toVal)}`,
      });
    }
  }

  // A half with no partner is a real one-sided filter ("created after X"), not
  // a broken range — emit it under its own operator.
  for (const item of [...betweenFrom.values(), ...betweenTo.values()].flat()) {
    if (!paired.has(item)) advancedFilters.push(chip(item, defOf(item.field)));
  }

  return advancedFilters;
};
